import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { Business, ContentCopy } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../contexts/AuthContext';
import type { Tenant } from '../types';


interface TenantResourceCopyDialogProps {
  open: boolean;
  title: string;
  description: string;
  submitting: boolean;
  error: string | null;
  sourceTenantId: string | null | undefined;
  onClose: () => void;
  onSubmit: (targetTenantId: string) => Promise<void> | void;
}

/**
 * Reusable dialog for privileged cross-tenant copy actions.
 */
export default function TenantResourceCopyDialog({
  open,
  title,
  description,
  submitting,
  error,
  sourceTenantId,
  onClose,
  onSubmit,
}: TenantResourceCopyDialogProps) {
  const { t } = useTranslation(['pages', 'common']);
  const { searchTenants } = useAuth();
  const [options, setOptions] = useState<Tenant[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const activeRequestRef = useRef(0);

  const filteredOptions = useMemo(
    () => options.filter((tenant) => tenant.id !== sourceTenantId),
    [options, sourceTenantId],
  );

  const loadTenants = useCallback(async (search: string, limit = 30) => {
    const requestId = activeRequestRef.current + 1;
    activeRequestRef.current = requestId;
    setLoading(true);
    try {
      const found = await searchTenants(search, limit);
      if (activeRequestRef.current !== requestId) {
        return;
      }
      setOptions(found);
    } finally {
      if (activeRequestRef.current === requestId) {
        setLoading(false);
      }
    }
  }, [searchTenants]);

  useEffect(() => {
    if (!open) {
      setSelectedTenant(null);
      setInputValue('');
      setOptions([]);
      return;
    }
    void loadTenants('', 30);
  }, [loadTenants, open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      void loadTenants(inputValue.trim());
    }, 350);
    return () => window.clearTimeout(timer);
  }, [inputValue, loadTenants, open]);

  const handleSubmit = async () => {
    if (!selectedTenant?.id || submitting) {
      return;
    }
    await onSubmit(selectedTenant.id);
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <ContentCopy sx={{ color: 'primary.main' }} />
          <Box component="span">{title}</Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>

        {error && <Alert severity="error">{error}</Alert>}

        <Autocomplete<Tenant, false, false, false>
          options={filteredOptions}
          value={selectedTenant}
          onChange={(_event, value) => {
            setSelectedTenant(value);
            setInputValue(value?.name ?? '');
          }}
          inputValue={inputValue}
          onInputChange={(_event, value, reason) => {
            if (reason === 'input' || reason === 'clear') {
              setInputValue(value);
            }
          }}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          loading={loading}
          noOptionsText={t('pages:resourceCopy.noResults')}
          renderOption={(props, option) => (
            <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Business fontSize="small" />
              {option.name}
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('pages:resourceCopy.targetTenant')}
              placeholder={t('pages:resourceCopy.targetTenantPlaceholder')}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          disabled={submitting}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          {t('common:actions.cancel')}
        </Button>
        <Button
          variant="contained"
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <ContentCopy />}
          onClick={handleSubmit}
          disabled={submitting || !selectedTenant?.id}
        >
          {submitting ? t('common:actions.saving') : t('pages:resourceCopy.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
